/**
 * Spaced Repetition Algorithm (SM-2)
 * Based on SuperMemo 2 algorithm - the same one used by Anki
 *
 * This algorithm schedules cards for optimal long-term retention
 */

/**
 * Calculate the next review date and update card stats
 * @param {Object} card - The flashcard object
 * @param {number} quality - User's response quality (0-5)
 *   5: Perfect response
 *   4: Correct response with hesitation
 *   3: Correct response with difficulty
 *   2: Incorrect but remembered
 *   1: Incorrect, barely remembered
 *   0: Complete blackout
 * @returns {Object} Updated card with new review data
 */
export function calculateNextReview(card, quality) {
    // Initialize card stats if not present
    const easinessFactor = card.easinessFactor || 2.5;
    const repetitions = card.repetitions || 0;
    const interval = card.interval || 0;

    let newEasinessFactor = easinessFactor;
    let newRepetitions = repetitions;
    let newInterval = interval;

    // Quality < 3 means the card was not remembered well
    if (quality < 3) {
        // Reset the card - start over
        newRepetitions = 0;
        newInterval = 0;
    } else {
        // Calculate new easiness factor
        newEasinessFactor = Math.max(
            1.3,
            easinessFactor + (0.1 - (5 - quality) * (0.08 + (5 - quality) * 0.02))
        );

        // Calculate new interval
        if (newRepetitions === 0) {
            newInterval = 1; // 1 day
        } else if (newRepetitions === 1) {
            newInterval = 6; // 6 days
        } else {
            newInterval = Math.round(interval * newEasinessFactor);
        }

        newRepetitions += 1;
    }

    // Calculate next review date
    const nextReviewDate = new Date();
    nextReviewDate.setDate(nextReviewDate.getDate() + newInterval);

    return {
        ...card,
        easinessFactor: newEasinessFactor,
        repetitions: newRepetitions,
        interval: newInterval,
        nextReviewDate: nextReviewDate.toISOString(),
        lastReviewedDate: new Date().toISOString(),
        totalReviews: (card.totalReviews || 0) + 1
    };
}

/**
 * Get quality score from user interaction
 * @param {string} userResponse - 'again', 'hard', 'good', 'easy'
 * @returns {number} Quality score (0-5)
 */
export function getQualityScore(userResponse) {
    const qualityMap = {
        'again': 0,      // Complete failure
        'hard': 2,       // Incorrect but recalled
        'good': 4,       // Correct with some hesitation
        'easy': 5        // Perfect recall
    };
    return qualityMap[userResponse] || 3;
}

/**
 * Filter cards that are due for review
 * @param {Array} cards - Array of flashcards
 * @returns {Array} Cards that are due for review today
 */
export function getDueCards(cards) {
    const now = new Date();
    return cards.filter(card => {
        if (!card.nextReviewDate) return true; // New cards are always due
        const reviewDate = new Date(card.nextReviewDate);
        return reviewDate <= now;
    });
}

/**
 * Get cards sorted by priority (due cards first, then by interval)
 * @param {Array} cards - Array of flashcards
 * @returns {Array} Sorted cards
 */
export function sortCardsByPriority(cards) {
    const now = new Date();
    return [...cards].sort((a, b) => {
        const aReviewDate = a.nextReviewDate ? new Date(a.nextReviewDate) : now;
        const bReviewDate = b.nextReviewDate ? new Date(b.nextReviewDate) : now;

        // Due cards come first
        const aDue = aReviewDate <= now;
        const bDue = bReviewDate <= now;

        if (aDue && !bDue) return -1;
        if (!aDue && bDue) return 1;

        // Among due cards, prioritize by how overdue they are
        if (aDue && bDue) {
            return aReviewDate - bReviewDate;
        }

        // Among future cards, show closest review date first
        return aReviewDate - bReviewDate;
    });
}

/**
 * Get statistics about the card deck
 * @param {Array} cards - Array of flashcards
 * @returns {Object} Statistics
 */
export function getDeckStats(cards) {
    const now = new Date();

    const newCards = cards.filter(c => !c.lastReviewedDate).length;
    const learningCards = cards.filter(c =>
        c.lastReviewedDate && c.repetitions < 3
    ).length;
    const matureCards = cards.filter(c => c.repetitions >= 3).length;
    const dueCards = cards.filter(c => {
        if (!c.nextReviewDate) return true;
        return new Date(c.nextReviewDate) <= now;
    }).length;

    return {
        total: cards.length,
        new: newCards,
        learning: learningCards,
        mature: matureCards,
        due: dueCards
    };
}

/**
 * Save cards to Firebase/localStorage
 * @param {string} userId - User ID
 * @param {string} topic - Topic/deck name
 * @param {Array} cards - Cards to save
 */
export async function saveCardsProgress(userId, topic, cards) {
    try {
        // Save to localStorage for now (can integrate with Firebase later)
        const key = `flashcards_${userId}_${topic.replace(/\s+/g, '_')}`;
        localStorage.setItem(key, JSON.stringify(cards));
        return { success: true };
    } catch (error) {
        console.error('Error saving cards:', error);
        return { success: false, error: error.message };
    }
}

/**
 * Load cards from Firebase/localStorage
 * @param {string} userId - User ID
 * @param {string} topic - Topic/deck name
 * @returns {Array} Loaded cards or null
 */
export function loadCardsProgress(userId, topic) {
    try {
        const key = `flashcards_${userId}_${topic.replace(/\s+/g, '_')}`;
        const saved = localStorage.getItem(key);
        return saved ? JSON.parse(saved) : null;
    } catch (error) {
        console.error('Error loading cards:', error);
        return null;
    }
}

// ============================================
// DECK-BASED FLASHCARD SYSTEM
// ============================================

/**
 * Save cards for a specific level/deck
 * @param {string} userId - User ID
 * @param {string} topic - Topic/path name
 * @param {number} level - Level number (1-based)
 * @param {Array} cards - Cards to save
 */
export async function saveDeckCards(userId, topic, level, cards) {
    try {
        const key = `flashdeck_${userId}_${topic.replace(/\s+/g, '_')}_L${level}`;
        localStorage.setItem(key, JSON.stringify(cards));

        // Also update the deck index
        updateDeckIndex(userId, topic, level);

        return { success: true };
    } catch (error) {
        console.error('Error saving deck cards:', error);
        return { success: false, error: error.message };
    }
}

/**
 * Load cards for a specific level/deck
 * @param {string} userId - User ID
 * @param {string} topic - Topic/path name
 * @param {number} level - Level number (1-based)
 * @returns {Array} Loaded cards or null
 */
export function loadDeckCards(userId, topic, level) {
    try {
        const key = `flashdeck_${userId}_${topic.replace(/\s+/g, '_')}_L${level}`;
        const saved = localStorage.getItem(key);
        return saved ? JSON.parse(saved) : null;
    } catch (error) {
        console.error('Error loading deck cards:', error);
        return null;
    }
}

/**
 * Update the deck index tracking which levels have decks
 */
function updateDeckIndex(userId, topic, level) {
    try {
        const indexKey = `flashdecks_index_${userId}_${topic.replace(/\s+/g, '_')}`;
        const existing = localStorage.getItem(indexKey);
        const levels = existing ? JSON.parse(existing) : [];

        if (!levels.includes(level)) {
            levels.push(level);
            levels.sort((a, b) => a - b);
            localStorage.setItem(indexKey, JSON.stringify(levels));
        }
    } catch (error) {
        console.error('Error updating deck index:', error);
    }
}

/**
 * Get all levels that have decks for a topic
 * @param {string} userId - User ID
 * @param {string} topic - Topic/path name
 * @returns {Array} Array of level numbers
 */
export function getDeckLevels(userId, topic) {
    try {
        const indexKey = `flashdecks_index_${userId}_${topic.replace(/\s+/g, '_')}`;
        const saved = localStorage.getItem(indexKey);
        return saved ? JSON.parse(saved) : [];
    } catch (error) {
        console.error('Error getting deck levels:', error);
        return [];
    }
}

/**
 * Get all decks (arrays of cards) for a topic
 * @param {string} userId - User ID
 * @param {string} topic - Topic/path name
 * @returns {Array} Array of card arrays for each level
 */
export function getAllDecksForPath(userId, topic) {
    try {
        const levels = getDeckLevels(userId, topic);
        return levels.map(level => loadDeckCards(userId, topic, level) || []);
    } catch (error) {
        console.error('Error getting all decks:', error);
        return [];
    }
}

/**
 * Load the master deck (all cards combined) for a topic
 * @param {string} userId - User ID
 * @param {string} topic - Topic/path name
 * @returns {Array} All cards from all levels
 */
export function loadMasterDeck(userId, topic) {
    try {
        const allDecks = getAllDecksForPath(userId, topic);
        return allDecks.flat();
    } catch (error) {
        console.error('Error loading master deck:', error);
        return [];
    }
}

/**
 * Get all cards for a topic (supporting both old and new storage)
 * @param {string} userId - User ID
 * @param {string} topic - Topic/path name
 * @param {string} mode - 'level', 'master', or 'all'
 * @param {number} level - Level number (required for 'level' mode)
 * @returns {Array} Cards to study
 */
export function getCardsForStudy(userId, topic, mode = 'all', level = null) {
    try {
        if (mode === 'level' && level) {
            return loadDeckCards(userId, topic, level) || [];
        }

        if (mode === 'master' || mode === 'all') {
            // Try new deck-based storage first
            const deckCards = loadMasterDeck(userId, topic);
            if (deckCards.length > 0) {
                return deckCards;
            }

            // Fall back to old storage format
            return loadCardsProgress(userId, topic) || [];
        }

        return [];
    } catch (error) {
        console.error('Error getting cards for study:', error);
        return [];
    }
}

/**
 * Save cards after study session (supporting deck-based storage)
 * @param {string} userId - User ID
 * @param {string} topic - Topic/path name
 * @param {Array} cards - Updated cards
 * @param {string} mode - 'level', 'master', or 'all'
 * @param {number} level - Level number (required for 'level' mode)
 */
export async function saveCardsAfterStudy(userId, topic, cards, mode = 'all', level = null) {
    try {
        if (mode === 'level' && level) {
            return await saveDeckCards(userId, topic, level, cards);
        }

        if (mode === 'master' || mode === 'all') {
            // For master/all mode, we need to update each card back to its original deck
            const levels = getDeckLevels(userId, topic);

            for (const lvl of levels) {
                const deckCards = loadDeckCards(userId, topic, lvl) || [];
                const deckCardIds = new Set(deckCards.map(c => c.id));

                // Find updated cards that belong to this deck
                const updatedDeckCards = cards.filter(c => deckCardIds.has(c.id));

                if (updatedDeckCards.length > 0) {
                    // Merge updates back into deck
                    const mergedCards = deckCards.map(original => {
                        const updated = updatedDeckCards.find(u => u.id === original.id);
                        return updated || original;
                    });

                    await saveDeckCards(userId, topic, lvl, mergedCards);
                }
            }

            // Also save to legacy format for backward compatibility
            await saveCardsProgress(userId, topic, cards);
        }

        return { success: true };
    } catch (error) {
        console.error('Error saving cards after study:', error);
        return { success: false, error: error.message };
    }
}
